import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useSingleMovieDetails from "../hooks/useSingleMovieDetails";
import { IMG_CDN_URL } from "../utils/constants";
import { useSelector, useDispatch } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";
import { resetSingleMovie } from "../utils/movieSlice";
import { CiCirclePlus, CiCircleCheck } from "react-icons/ci";
import { addToWishlist, removeFromWishlist } from "../utils/movieSlice";
import play from "../../src/assets/images/play.svg";
import Modal from "./Modal";

const SingleMovie = () => {
	const { movieId } = useParams(); // Get the dynamic movie ID from the URL
	const dispatch = useDispatch();

	const wishlist = useSelector((state) => state.movies.wishlist); // Get wishlist from Redux store
	const [showVideoModal, setShowVideoModal] = useState(false);

	const handlePlayClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setShowVideoModal(true);
	};

	const handleCloseModal = () => {
		setShowVideoModal(false);
	};

	// Custom hook to fetch movie details
	useSingleMovieDetails(movieId);

	// Fetch movie data from the Redux store
	const movieData = useSelector((store) => store.movies?.singleMovie);

	// Clear movie data on unmount
	useEffect(() => {
		return () => {
			dispatch(resetSingleMovie());
		};
	}, [dispatch]);

	// Show a loader if movie data isn't available yet
	if (!movieData) {
		return (
			<>
				<Header />
				<div className="movie-single max-w-[1280px] m-auto w-full relative pt-[50px] md:pt-[84px] lg:pt-[96px] pb-[100px]">
					<div className="movie-container flex flex-col lg:flex-row gap-16 bg-black text-white h-full lg:h-[70vh] px-4 overflow-hidden">
						{/* Placeholder for Movie Poster with fixed dimensions */}
						<div className="movie-poster order-2 lg:order-1 flex items-center justify-center overflow-hidden flex-none w-full lg:w-1/3 h-96 lg:h-auto">
							<div className="animate-pulse bg-white opacity-10 w-full h-full rounded-lg"></div>
						</div>

						{/* Placeholder for Movie Content with same structure */}
						<div className="movie-content lg:order-2 flex flex-col flex-[1_1_50%] justify-center gap-8 basis-[60%] overflow-y-auto">
							{/* Title and Overview placeholders */}
							<div>
								<div className="animate-pulse bg-white opacity-10 h-12 w-3/4 mb-4 rounded"></div>
								<div className="animate-pulse bg-white opacity-10 h-32 w-full rounded"></div>
							</div>

							{/* Genres placeholder */}
							<div className="animate-pulse bg-white opacity-10 h-6 w-1/2 rounded"></div>

							{/* Stats placeholders */}
							<div className="flex justify-between [@media(width<=1100px)]:max-w-full [@media(width>1100px)]:max-w-[50%] w-full">
								<div className="animate-pulse bg-white opacity-10 h-6 w-16 rounded"></div>
								<div className="animate-pulse bg-white opacity-10 h-6 w-20 rounded"></div>
								<div className="animate-pulse bg-white opacity-10 h-6 w-32 rounded"></div>
								<div className="animate-pulse bg-white opacity-10 h-6 w-6 rounded-full"></div>
							</div>
						</div>
					</div>
				</div>
				<Footer />
			</>
		);
	}

	const {
		poster_path,
		title,
		overview,
		vote_average,
		runtime,
		release_date,
		genres,
	} = movieData;

	// Calculate runtime in hours and minutes
	const hours = Math.floor(runtime / 60);
	const minutes = runtime % 60;

	// Format release date as "30 April 2024"
	const releaseDate = new Date(release_date).toLocaleDateString("en-GB", {
		day: "numeric",
		month: "long",
		year: "numeric",
	});

	const genreIds = movieData?.genres?.map((genre) => genre.id) || [];

	const updatedMovieData = {
		...movieData,
		genre_ids: genreIds,
	};

	const isMovieInWishlist = wishlist.some((m) => m.id === Number(movieId));

	const handleWishlistToggle = (e) => {
		e.preventDefault();

		if (isMovieInWishlist) {
			// If the movie is in the wishlist, remove it
			dispatch(removeFromWishlist(Number(movieId)));
		} else {
			// If the movie is not in the wishlist, add it
			dispatch(addToWishlist(updatedMovieData));
		}
	};

	return (
		<>
			<Header />
			<div className="movie-single m-auto relative px-4 pt-[80px] md:pt-[84px] lg:pt-[96px] pb-[100px]">
				<div className="movie-container max-w-[1280px] flex flex-col lg:flex-row gap-16 bg-black text-white h-full pt-4 md:pt-0 overflow-hidden">
					{/* Movie Poster - with the same dimensions as the placeholder */}
					<div className="movie-poster order-2 lg:order-1 flex items-center justify-center overflow-hidden flex-none w-full lg:w-1/3 lg:h-auto">
						<img
							alt="Movie Poster"
							src={`${IMG_CDN_URL}${poster_path}`}
							className="w-full h-full object-contain object-left rounded-lg"
						/>
					</div>

					{/* Movie Content */}
					<div className="movie-content lg:order-2 flex flex-col flex-[1_1_50%] justify-center gap-8 basis-[60%] overflow-y-auto">
						{/* Title and Overview */}
						<div>
							<h2 className="text-[clamp(1.5rem,0.582rem+2.449vw,3.25rem)] pb-4 font-montserrat leading-tight font-semibold">
								{title}
							</h2>
							<p className="text-xl 2xl:text-2xl ">{overview}</p>
						</div>

						{/* Genres */}
						<div className="2xl:text-2xl ">
							<span>{genres.map((genre) => genre.name).join(" | ")}</span>
						</div>

						{/* Stats */}
						<div className="2xl:text-xl flex justify-between [@media(width<=1100px)]:max-w-full [@media(width>1100px)]:max-w-[50%] w-full">
							<span>⭐️ {Math.floor(vote_average)}/10</span>
							<span>
								{hours}hr {minutes}min
							</span>
							<span>{releaseDate}</span>
							{isMovieInWishlist ? (
								<CiCircleCheck
									size={24}
									className="text-green-400 hover:text-green-500 cursor-pointer wishlist-icon"
									onClick={handleWishlistToggle}
								/>
							) : (
								<CiCirclePlus
									size={24}
									className="text-gray-400 hover:text-white cursor-pointer wishlist-icon"
									onClick={handleWishlistToggle}
								/>
							)}
						</div>

						<button
							className=" w-fit flex items-center text-xs md:text-base gap-2 px-3 py-2 md:px-5 md:py-3 rounded-lg bg-white text-black hover:opacity-80"
							alt="Play button"
							data-play-button="true"
							onClick={handlePlayClick}
						>
							<div>
								<img className="w-[16px]" src={play} alt="Play Video" />
							</div>
							<span className="2xl:text-2xl">Play</span>
						</button>
					</div>
				</div>
			</div>
			{showVideoModal && <Modal movieId={movieId} onClose={handleCloseModal} />}
			<Footer />
		</>
	);
};

export default SingleMovie;
